import { EmailTemplateDTO, type JobPostingDTO } from "@pentacode/core";
import { css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import type { RichTextEditor } from "../../../components/rich-text/rich-text-editor";
import { Dialog } from "../../../elements/dialog";
import "../../../elements/scroller";

type JobPostingEmailTemplateDialogResult = {
    action: "save" | "discard";
    template?: EmailTemplateDTO;
};

@customElement("ptc-job-posting-email-template-dialog")
export class JobPostingEmailTemplateDialog extends Dialog<
    { template: EmailTemplateDTO; posting: JobPostingDTO },
    JobPostingEmailTemplateDialogResult
> {
    @state()
    private _templateTypeTitle = "Mail Vorlage";

    @state()
    private _title = "";

    @state()
    private _body = "";

    @state()
    private _replyTo = "";

    @state()
    private _wasChanged = false;

    @query("ptc-rich-text-editor")
    private _editor: RichTextEditor;

    async show({ template, posting }: { template: EmailTemplateDTO; posting: JobPostingDTO }) {
        this._title = template?.title ?? "";
        this._body = template?.body ?? "";
        this._replyTo = template?.replyTo ?? "";
        this._templateTypeTitle = posting?.title ? `Mail Vorlage für ${posting.title}` : "Mail Vorlage";
        this._wasChanged = false;

        // tiptap (~564KB, vendor-editor) loads on demand when the dialog opens; await
        // it so <ptc-rich-text-editor> is defined before the template queries it below.
        await import("../../../components/rich-text/rich-text-editor");
        const promise = super.show({ template, posting });
        await this.updateComplete;
        if (this._editor) {
            this._editor.content = this._body;
        }
        this._wasChanged = false;

        return promise;
    }

    private _serializeBody() {
        const doc = new DOMParser().parseFromString(this._body || "", "text/html");
        const cleanedBody = doc.body.innerHTML.trim();
        return cleanedBody;
    }

    private _hasBodyContent(body: string) {
        const doc = new DOMParser().parseFromString(body || "", "text/html");
        return Boolean(doc.body.textContent?.trim());
    }

    private get _canSave() {
        return this._wasChanged && Boolean(this._title.trim() && this._hasBodyContent(this._body));
    }

    private _bodyChanged(e: CustomEvent<{ content: string }>) {
        this._body = e.detail.content;
        this._wasChanged = true;
    }

    private _titleChanged(e: Event) {
        this._title = (e.target as HTMLInputElement).value;
        this._wasChanged = true;
    }

    private _replyToChanged(e: Event) {
        this._replyTo = (e.target as HTMLInputElement).value;
        this._wasChanged = true;
    }

    private _save(e: Event) {
        e.preventDefault();
        if (!this._canSave) {
            return;
        }

        this.done({
            action: "save",
            template: new EmailTemplateDTO({
                title: this._title.trim(),
                body: this._serializeBody(),
                replyTo: this._replyTo.trim() || undefined,
            } as EmailTemplateDTO),
        });
    }

    private _discard() {
        this.done({ action: "discard" });
    }

    static styles = [
        ...Dialog.styles,
        css`
            label {
                padding: 0;
                margin: 0.5em;
                color: var(--color-primary);
            }

            ptc-rich-text-editor {
                min-height: 16em;
                max-height: 22em;
            }

            .images {
                display: flex;
                align-items: flex-start;
                gap: 0.5em;
            }

            .image-container {
                width: 10em;
                aspect-ratio: 16 / 9;
                position: relative;
                border-radius: var(--border-radius);
                overflow: hidden;
                background: var(--shade-1);
                border: 1px dashed var(--shade-2);
            }

            .image-container img {
                position: absolute;
                inset: 0;
                object-fit: contain;
                object-position: center center;
            }

            .image-container button {
                position: absolute;
                top: 0.4em;
                right: 0.4em;
                background: var(--color-bg) !important;
                opacity: 1 !important;
                padding: 0.2em;
            }

            .image-container:not(:hover) button {
                display: none;
            }

            .upload-image-button {
                width: 3.2em;
                height: 3.2em;
                min-height: 3.2em;
                padding: 0;
                border: 1px dashed var(--shade-2);
                background: var(--shade-1);
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .dialog-title {
                font-weight: 600;
            }
        `,
    ];

    renderContent() {
        return html`
            <form class="vertical layout fit-vertically" @submit=${this._save}>
                <div class="padded horizontal layout">
                    <div class="padded stretch dialog-title">${this._templateTypeTitle}</div>
                    <button class="slim transparent" type="button" @click=${this._discard}>
                        <ptc-ui-icon name="icon.action.close"></ptc-ui-icon>
                    </button>
                </div>

                <ptc-scroller class="stretch">
                    <div class="padded vertical spacing layout">
                        <div class="field">
                            <label>Titel</label>
                            <input
                                class="title slim"
                                .value=${this._title}
                                @input=${this._titleChanged}
                                placeholder="Titel der E-Mail"
                                maxlength="180"
                                required
                            />
                        </div>

                        <div class="field">
                            <label>Antwort an</label>
                            <input
                                class="slim"
                                type="email"
                                .value=${this._replyTo}
                                @input=${this._replyToChanged}
                                placeholder="antwort@unternehmen.de"
                            />
                        </div>

                        <div class="field stretch vertical layout">
                            <label>Nachricht</label>
                            <ptc-rich-text-editor
                                class="stretch"
                                @content-changed=${this._bodyChanged}
                            ></ptc-rich-text-editor>
                        </div>
                    </div>
                </ptc-scroller>

                <div class="padded horizontal evenly stretching spacing layout">
                    <button class="primary" type="submit" ?disabled=${!this._canSave}>
                        <ptc-ui-icon name="icon.action.save"></ptc-ui-icon> Speichern
                    </button>
                    <button class="transparent" type="button" @click=${this._discard}>Abbrechen</button>
                </div>
            </form>
        `;
    }
}

//@TODO: Fix the logo/signature attachments to have correct sizing and be used in the future
